import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import HelloSign from "hellosign-embedded";
import {
  Button,
  Colors,
  fontMedium,
  Icon,
  LoadingInterstitial,
  useFeatureFlags,
} from "swing-components";

import { ErrorMessage } from "~components";
import { SignatureAnswerType } from "~onboarding/utils";
import { GET, HELLOSIGN_CLIENT_ID, translate } from "~utils";

const helloSignClient = new HelloSign({ clientId: HELLOSIGN_CLIENT_ID });

type SignatureAnswerProps = Omit<SignatureAnswerType, "type"> & {
  /**
   * Function to be called when signing
   */
  onChange: (value: string) => void;
  /**
   * Disables the ability to open HelloSign
   */
  isReadOnly?: boolean;
};

/**
 * Set to true to enable test mode during development.
 * See https://developers.hellosign.com/api/reference/operation/signatureRequestCreateEmbedded/#!ct=application/json&path=test_mode&t=request
 */
const DEBUG = process.env.NODE_ENV === "development";

type SignatureUrlResponse = {
  signatureId?: string | null;
  signatureUrl?: string | null;
};

/**
 *
 * NOTE: That the `value` being passed to this component is the one from the
 * answerType definition which is generally `valid`, thus, the value prop in
 * this situation is never the `signatureId` (or the "answer" you'd generally
 * expect with other `value` props for other answer types).
 *
 * So note that even though we are not using the `value` prop, the Step/Question
 * will know that this is answered and render it as a `readOnly` answer.
 */
export function SignatureAnswer(props: SignatureAnswerProps) {
  const { isLoading, flags, error } = useFeatureFlags();

  if (isLoading) {
    return (
      <div css={{ padding: 32 }}>
        <LoadingInterstitial />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage>
        <span>
          {translate("SIGNATURE_URL_API_ERROR_MESSAGE")}{" "}
          <a target="_blank" rel="noreferrer" href="mailto:support@swingeducation.com">
            {translate("LABEL_CONTACT_SUPPORT")}
          </a>
          .
        </span>
      </ErrorMessage>
    );
  }

  // If something goes wrong, we can turn off this FF so that the old flow continues
  if (flags.signatureAnswerV2 === false) {
    return <SignatureAnswerOld {...props} />;
  }

  return <SignatureAnswerNew {...props} />;
}

const DEFAULT_SIGNED_MESSAGE = "Signed! Click Submit and Continue to proceed.";

function SignatureAnswerNew(props: SignatureAnswerProps) {
  const {
    document,
    onChange,
    label,
    signedMessage = DEFAULT_SIGNED_MESSAGE,
    isReadOnly = false,
  } = props;

  /** State ***/
  const [signatureUrl, setSignatureUrl] = useState<string | undefined>();
  const [isSigned, setIsSigned] = useState(false);

  /***** Queries *****/
  const {
    refetch: fetchSignatureUrl,
    isFetching,
    isError,
  } = useQuery<
    { data: SignatureUrlResponse },
    AxiosError<{ message: string }>,
    SignatureUrlResponse
  >({
    enabled: false,
    queryKey: ["signatureUrl"],
    staleTime: 0,
    queryFn: () =>
      GET("/api/sub/signature-url", {
        queryParams: {
          "test-mode": DEBUG,
          // Passing the attribute as a search param so that the API can
          // determine which document url to return.
          document,
        },
      }),
    select: (data) => data.data,
  });

  /*** Effects ***/
  // When the component mounts
  useEffect(() => {
    // Get the signature URL, and handle if the document is already signed.
    getSignatureURL();

    // Create event listeners.
    // When signing the document, pass the signature id to the parent.
    helloSignClient.on("sign", (data) => {
      onChange(data.signatureId);
      setIsSigned(true);
    });

    // When opening the HelloSign modal, delete the signature url since it can
    // only be used once.
    helloSignClient.on("open", () => setSignatureUrl(undefined));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*** Helpers ***/
  /** Gets the signature url, and handle if the document is already signed. */
  async function getSignatureURL() {
    // Fetch the signature url
    const { data } = await fetchSignatureUrl();

    // If there is a `signatureId`, the document was already signed outside
    // of the platform.
    if (data?.signatureId) {
      // Tell the parent that the document was already signed.
      onChange(data.signatureId);
      // Set the signed state to true to show the success message.
      setIsSigned(true);
    }
    // If the document is not signed, set the signature url.
    else if (data?.signatureUrl) {
      setSignatureUrl(data?.signatureUrl);
    }

    return Promise.resolve(data?.signatureUrl || undefined);
  }

  /***** Handlers *****/
  async function handleClick() {
    // If there is no signature url, which happens when clicking on the button
    // for the second time, refetch the signature url.
    let url = signatureUrl;
    if (!url) {
      url = await getSignatureURL();
    }

    if (url) {
      if (DEBUG) {
        helloSignClient.open(url, {
          debug: true,
          skipDomainVerification: true,
          testMode: true,
        });
      } else {
        helloSignClient.open(url);
      }
    }
  }

  /***** Hooks *****/
  return (
    <>
      {isError && (
        <ErrorMessage>
          <span>
            {translate("SIGNATURE_URL_API_ERROR_MESSAGE")}{" "}
            <a target="_blank" rel="noreferrer" href="mailto:support@swingeducation.com">
              {translate("LABEL_CONTACT_SUPPORT")}
            </a>
            .
          </span>
        </ErrorMessage>
      )}
      {isSigned && (
        <div css={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Icon name="Check Circle" color={Colors.green500} />
          <span css={fontMedium({ fontWeight: 600, color: "green500" })}>{signedMessage}</span>
        </div>
      )}

      <Button
        type="primary"
        status={isReadOnly || isError ? "disabled" : isFetching ? "loading" : "enabled"}
        isFluid
        onClick={handleClick}
      >
        {label}
      </Button>
    </>
  );
}

/***** Old SignatureAnswer (Delete After FF Validates) *****/
export function SignatureAnswerOld(props: SignatureAnswerProps) {
  const {
    document,
    onChange,
    value,
    label,
    signedMessage = DEFAULT_SIGNED_MESSAGE,
    isReadOnly = false,
  } = props;

  /** State ***/
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [isAlreadySigned, setIsAlreadySigned] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  /***** Queries *****/
  const { refetch: getSignatureUrl } = useQuery({
    enabled: false,
    queryKey: ["signatureUrl"],
    queryFn: () =>
      GET("/api/sub/signature-url", {
        queryParams: {
          "test-mode": DEBUG,
          // Passing the attribute as a search param so that the API can
          // determine which document url to return.
          document,
        },
      }),
    select: (data) => data.data,
  });

  /***** Handlers *****/
  function handleSigned(signatureId?: string) {
    // Pass to the Question component the signatureId of the signed document
    onChange(signatureId || value);
  }

  function handleClick() {
    if (signatureUrl) {
      if (DEBUG) {
        helloSignClient.open(signatureUrl, {
          debug: true,
          skipDomainVerification: true,
          testMode: true,
        });
      } else {
        helloSignClient.open(signatureUrl);
      }
    }
  }

  /***** Hooks *****/
  useEffect(() => {
    // This is to appease the TypeScript Compiler, due to the 'sign' event passing
    // data to its callback, which is not used here.
    const onSign = () => {
      handleSigned();
    };
    getSignatureUrl()
      .then(({ data }) => {
        // This indicates that the document was already signed but the user
        // didn't click the Submit button to finalize this Step.
        // By calling onChange, the Step will be marked as complete.
        if (data?.signatureId) {
          handleSigned(data?.signatureId);
          setIsAlreadySigned(true);
        } else if (data?.signatureUrl) {
          setSignatureUrl(data?.signatureUrl);
          helloSignClient.on("sign", onSign);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
    return () => {
      helloSignClient.off("sign", onSign);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isAlreadySigned && (
        <div css={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Icon name="Check Circle" color={Colors.green500} />
          <span css={fontMedium({ fontWeight: 600, color: "green500" })}>{signedMessage}</span>
        </div>
      )}
      <Button
        type="primary"
        status={isReadOnly ? "disabled" : isLoading ? "loading" : "enabled"}
        isFluid
        onClick={handleClick}
      >
        {label}
      </Button>
    </>
  );
}
